/**
 * Standalone matching-product controller.
 *
 * The main PDP owns its selected variant. This component only adds the chosen
 * Shopify Complementary product variant using ordinary Shopify cart semantics.
 */
(function registerProductPairing() {
  if (customElements.get('product-pairing')) return;

  class ProductPairing extends HTMLElement {
    constructor() {
      super();
      this.cardStates = new Map();
      this.viewObserver = null;
      this.viewEmitted = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelectorAll('[data-pairing-card]').forEach((card) => this.initializeCard(card));
      this.addEventListener('click', (event) => this.handleClick(event));
      this.startViewObservation();
    }

    disconnectedCallback() {
      this.viewObserver?.disconnect();
      this.viewObserver = null;
    }

    parseJson(selector, fallback, scope = this) {
      const node = scope.querySelector(selector);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return fallback;
      }
    }

    emitUiEvent(action, detail = {}) {
      const section = this.querySelector('[data-complete-look]');
      this.dispatchEvent(new CustomEvent('bohemia:complete-look-ui', {
        bubbles: true,
        detail: {
          action,
          productId: Number(this.dataset.analyticsProductId) || null,
          pairingProductId: Number(detail.card?.dataset.pairingProductId) || null,
          treatment: this.dataset.completeLookTreatment,
          layout: section?.dataset.completeLookLayout || '',
          source: section?.dataset.pairSource || '',
          piece: detail.piece || '',
          cartAction: 'add_to_bag',
          optionIndex: Number.isFinite(Number(detail.optionIndex)) ? Number(detail.optionIndex) : null,
        },
      }));
    }

    startViewObservation() {
      if (this.viewObserver || this.viewEmitted) return;
      const section = this.querySelector('[data-complete-look]');
      if (!section || !('IntersectionObserver' in window)) return;
      this.viewObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        this.viewEmitted = true;
        this.emitUiEvent('view');
        this.viewObserver?.disconnect();
        this.viewObserver = null;
      }, { threshold: [0.5] });
      this.viewObserver.observe(section);
    }

    initializeCard(card) {
      const variants = this.parseJson('[data-pairing-variants]', [], card);
      const availability = this.parseJson('[data-pairing-availability]', {}, card);
      const initialId = Number(card.dataset.selectedVariantId);
      const initialVariant = variants.find((variant) => Number(variant.id) === initialId)
        || variants.find((variant) => this.isRetailAvailable(variant, availability))
        || variants[0]
        || null;

      this.cardStates.set(card, {
        variants,
        availability,
        selectedOptions: initialVariant ? [...initialVariant.options] : [],
        selectedVariant: initialVariant,
        adding: false,
      });
      this.updateCard(card);
    }

    handleClick(event) {
      const option = event.target.closest('[data-pairing-option]');
      if (option && this.contains(option)) {
        this.selectOption(option);
        return;
      }

      const addButton = event.target.closest('[data-pairing-add]');
      if (addButton && this.contains(addButton)) this.addPairing(addButton);
    }

    selectOption(button) {
      if (button.disabled) return;
      const card = button.closest('[data-pairing-card]');
      const state = this.cardStates.get(card);
      if (!state) return;

      const optionIndex = Number(button.dataset.optionIndex);
      if (state.selectedOptions[optionIndex] === button.dataset.optionValue) return;
      state.selectedOptions[optionIndex] = button.dataset.optionValue;
      state.selectedVariant = state.variants.find((variant) =>
        variant.options.every((value, index) => value === state.selectedOptions[index])
      ) || null;
      this.emitUiEvent('size_selected', { card, piece: 'matching', optionIndex });
      this.updateCard(card);
    }

    updateCard(card) {
      const state = this.cardStates.get(card);
      if (!state) return;

      card.querySelectorAll('[data-pairing-option]').forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex);
        const optionValue = button.dataset.optionValue;
        const selected = state.selectedOptions[optionIndex] === optionValue;
        const available = state.variants.some((variant) => {
          if (!this.isRetailAvailable(variant, state.availability)) return false;
          return variant.options.every((value, index) =>
            index === optionIndex ? value === optionValue : value === state.selectedOptions[index]
          );
        });
        button.disabled = !available;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const price = card.querySelector('[data-pairing-price]');
      if (price && state.selectedVariant) {
        price.textContent = this.formatMoney(Number(state.selectedVariant.price || 0));
      }

      const selectedAvailability = this.availabilityForVariant(state.selectedVariant, state.availability);
      const extendedLead = card.querySelector('[data-pairing-extended-lead]');
      const extendedLeadLabel = card.querySelector('[data-pairing-extended-lead-label]');
      if (extendedLead) extendedLead.hidden = selectedAvailability?.extendedLead !== true;
      if (extendedLeadLabel && selectedAvailability?.extendedLead === true) {
        extendedLeadLabel.textContent = `${card.dataset.extendedLeadStatus} · ${card.dataset.extendedLeadDispatchTemplate.replace('__date__', selectedAvailability.dispatchDisplay)}`;
      }
      const legacyPreorder = card.querySelector('[data-pairing-legacy-preorder]');
      if (legacyPreorder) legacyPreorder.hidden = selectedAvailability?.legacyPreorder !== true;

      const addButton = card.querySelector('[data-pairing-add]');
      if (!addButton) return;
      const available = this.isRetailAvailable(state.selectedVariant, state.availability);
      addButton.disabled = state.adding || !available;
      addButton.setAttribute('aria-busy', state.adding ? 'true' : 'false');
      const label = addButton.querySelector('[data-pairing-add-label]');
      if (label) {
        label.textContent = state.adding
          ? addButton.dataset.addingLabel
          : addButton.dataset.defaultLabel;
      }
    }

    availabilityForVariant(variant, availability) {
      if (!variant) return null;
      return availability[String(variant.id)] || availability[variant.id] || null;
    }

    isRetailAvailable(variant, availability) {
      if (!variant || variant.available !== true) return false;
      return this.availabilityForVariant(variant, availability)?.available === true;
    }

    pairingProperties(card, variant) {
      const state = this.cardStates.get(card);
      const availability = this.availabilityForVariant(variant, state?.availability || {});
      return availability?.properties ? { ...availability.properties } : null;
    }

    async addPairing(button) {
      const card = button.closest('[data-pairing-card]');
      const state = this.cardStates.get(card);
      const errorNode = card?.querySelector('[data-pairing-error]');
      const statusNode = card?.querySelector('[data-pairing-status]');
      if (!card || !state || state.adding) return;

      const pairingVariant = state.selectedVariant;
      if (!this.isRetailAvailable(pairingVariant, state.availability)) return;
      const item = { id: Number(pairingVariant.id), quantity: 1 };
      const properties = this.pairingProperties(card, pairingVariant);
      if (properties) item.properties = properties;

      state.adding = true;
      this.emitUiEvent('add_attempt', { card });
      if (errorNode) {
        errorNode.hidden = true;
        errorNode.textContent = '';
      }
      if (statusNode) statusNode.textContent = '';
      this.updateCard(card);

      try {
        const success = await this.addItems([item]);
        if (!success) throw new Error(this.cartErrorMessage());
        this.emitUiEvent('add_success', { card });
        if (statusNode) statusNode.textContent = this.dataset.addedMessage || '';
        window.dispatchEvent(new CustomEvent('cart:open'));
      } catch (error) {
        if (errorNode) {
          errorNode.textContent = error?.message || this.dataset.errorMessage;
          errorNode.hidden = false;
        }
        this.emitUiEvent('add_error', { card });
      } finally {
        state.adding = false;
        this.updateCard(card);
      }
    }

    async addItems(items) {
      const cart = window.Alpine?.store('cart');
      if (cart?.addItems) return cart.addItems(items);

      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || this.dataset.errorMessage);
      }

      window.location.assign(this.dataset.cartUrl);
      return true;
    }

    cartErrorMessage() {
      return window.Alpine?.store('cart')?.errorMessage || this.dataset.errorMessage;
    }

    formatMoney(cents) {
      const cart = window.Alpine?.store('cart');
      if (cart?.formatMoney) return cart.formatMoney(cents);
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency,
      }).format(cents / 100);
    }
  }

  customElements.define('product-pairing', ProductPairing);
})();
